/**
 * CampaignSettingsCard Component
 * Displays campaign timezone setting with a dropdown selector.
 * Only visible to Owner/Manager roles.
 */

import { useState } from 'react';
import { Globe, Loader2, Check } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { TenantCampaignWithCapabilitiesDto } from '@signalvote/shared';
import { useCampaignStore } from '@/stores/campaignStore';
import { useUpdateTenantCampaign } from '@/hooks/queries/useTenantCampaigns';
import { cn } from '@/lib/utils';

// Common US/IANA timezones with friendly labels
const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (America/New_York)' },
  { value: 'America/Chicago', label: 'Central Time (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska Time (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (Pacific/Honolulu)' },
  { value: 'America/Phoenix', label: 'Arizona - No DST (America/Phoenix)' },
  { value: 'America/Indiana/Indianapolis', label: 'Indiana Eastern (America/Indiana/Indianapolis)' },
  { value: 'America/Detroit', label: 'Detroit Eastern (America/Detroit)' },
  { value: 'America/Boise', label: 'Boise Mountain (America/Boise)' },
  { value: 'America/Adak', label: 'Hawaii-Aleutian (America/Adak)' },
  { value: 'America/Nome', label: 'Nome Alaska (America/Nome)' },
  { value: 'US/Samoa', label: 'Samoa Time (US/Samoa)' },
  { value: 'Pacific/Guam', label: 'Guam/Chamorro (Pacific/Guam)' },
] as const;

export function CampaignSettingsCard() {
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  const canManageTeam = useCampaignStore((s) => s.canManage('TeamManagement'));

  if (!selectedCampaign || !canManageTeam) return null;

  // Keyed on the campaign: switching campaigns starts fresh, while a refetch of
  // the SAME campaign leaves an unsaved pick alone.
  return <TimezoneForm key={selectedCampaign.id} selectedCampaign={selectedCampaign} />;
}

function TimezoneForm({ selectedCampaign }: { selectedCampaign: TenantCampaignWithCapabilitiesDto }) {
  const currentTimezone = selectedCampaign.timeZoneId || 'America/New_York';
  const [selectedTimezone, setSelectedTimezone] = useState(currentTimezone);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const updateCampaign = useUpdateTenantCampaign();

  const hasChanges = selectedTimezone !== currentTimezone;

  const handleSave = () => {
    setSaveSuccess(false);
    updateCampaign.mutate(
      { id: selectedCampaign.id, data: { timeZoneId: selectedTimezone } },
      {
        onSuccess: () => {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Globe className="h-5 w-5" />
          Campaign Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Campaign Name (read-only) */}
        <div>
          <label className="text-sm font-medium text-muted-foreground">Campaign Name</label>
          <p className="text-sm mt-0.5">{selectedCampaign.name}</p>
        </div>

        {/* Timezone Selector */}
        <div>
          <label htmlFor="timezone-select" className="text-sm font-medium text-muted-foreground">
            Campaign Timezone
          </label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">
            All calendar times and shift schedules use this timezone.
          </p>
          <select
            id="timezone-select"
            value={selectedTimezone}
            onChange={(e) => {
              setSelectedTimezone(e.target.value);
              setSaveSuccess(false);
            }}
            className={cn(
              'w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            )}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>

        {/* Save Button */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || updateCampaign.isPending}
          >
            {updateCampaign.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Saving...
              </>
            ) : (
              'Save Timezone'
            )}
          </Button>
          {saveSuccess && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <Check className="h-4 w-4" />
              Saved
            </span>
          )}
          {updateCampaign.isError && (
            <span className="text-sm text-destructive">
              Failed to save. Please try again.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default CampaignSettingsCard;
